import React, { useMemo, useEffect, useRef } from 'react';
import { Spinner } from '../../../../common';
import { useSelector, useDispatch } from 'react-redux';
import { showErrorPage } from '../../../../../redux/slices/coreSlice';
import NewsForm from './NewsForm.jsx';
import { routes } from '../../../../../utils/helpers/ApiClient';
import {
  formattedFullName,
  getBooleanValue,
  isUserAndItsMorphIdentical,
} from '../../../../../utils/helpers/CommonHelper';
import { AppBarCMS } from '../../../../basic/';
import { useTranslation } from 'react-i18next';
import usePermission from '../../../../../hooks/usePermission';
import usePrepareOutputData from './usePrepareOutputData';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  useGetNewsDetailQuery,
  useGetNewsCategoriesQuery,
  useSaveNewsMutation,
} from '../../../../../redux/services/news';
import dayjs from 'dayjs';
import { has } from 'lodash';
import useIsUserAdmin from '../../../../../hooks/useIsUserAdmin';
import { DEFAULT_IMAGE } from '../../../../../utils/constants/common_constants.js';
import checkPermission from '../../../../../utils/helpers/checkPermission';
import useSecondaryActions from '../../../../../hooks/useSecondaryActions';
import { useToastMessages } from '../../../../../hooks/useToastMessages.jsx';

const NewsEdit = ({ mode }) => {
  const { id } = useParams();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const prepareOutputData = usePrepareOutputData();
  const { saveSuccess } = useToastMessages();
  const isAdmin = useIsUserAdmin();
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const location = useLocation();
  const initialNavigateTo =
    (location.state?.from?.pathname || '') +
      (location.state?.from?.search || '') || routes.cmsNews;
  const navigateTo = useRef(initialNavigateTo);

  const {
    //mainActions,
    setFormDisabledStates,
    visibleElement,
    setVisibleElement,
  } = useSecondaryActions();

  const user = useSelector((reduxStore) => reduxStore.auth.user);
  const currentUserPersonId = user?.person_id ?? user?.id;
  const currentUserPersonIdForForm =
    currentUserPersonId === null || currentUserPersonId === undefined
      ? undefined
      : String(currentUserPersonId);
  // show is_top only when user is admin, or has news-admin permission
  const isNewsAdmin = usePermission('news-admin') || isAdmin;

  const { data: newsCategories, isLoading: isLoadingNewsCategories } =
    useGetNewsCategoriesQuery();
  const { data: newsDetail, isLoading } = useGetNewsDetailQuery(id, {
    skip: !isEdit,
  });
  const [saveNews, { isSuccess: isSaveSuccess }] = useSaveNewsMutation();

  useEffect(() => {
    document.title = isEdit
      ? t('Úprava aktuality') + ' - FRI CMS'
      : t('Pridanie aktuality') + ' - FRI CMS';
  }, []); // Runs on mount

  useEffect(() => {
    if (isSaveSuccess) {
      saveSuccess();
      // If we're in CMS, always go back to CMS news list
      if (location.pathname.startsWith('/cms')) {
        navigate(routes.cmsNews);
      } else {
        navigate(navigateTo.current || routes.cmsNews);
      }
    }
  }, [isSaveSuccess, location.pathname]);

  useEffect(() => {
    if (isEdit && !isLoading) {
      // If is admin or author with permissions to edit

      const canUpdate =
        isAdmin ||
        (isUserAndItsMorphIdentical(user, newsDetail.person) &&
          checkPermission(user, 'news.update'));

      if (!canUpdate) {
        dispatch(
          showErrorPage({
            status: 403,
            message: t('Chýbajúce oprávnenia'),
          }),
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }
  }, [isLoading, dispatch]);

  const initials = useMemo(
    () =>
      (isEdit && !newsDetail) || !newsCategories
        ? undefined
        : {
            id: null,
            title_sk: null,
            title_en: null,
            slug_sk: null,
            slug_en: null,
            content_sk: null,
            content_en: null,
            image_name: DEFAULT_IMAGE,
            person_id:
              newsDetail && newsDetail.person_id
                ? String(newsDetail.person_id)
                : currentUserPersonIdForForm,
            share_btn: null,
            ...(newsDetail || {}),
            display_sk: newsDetail
              ? getBooleanValue(newsDetail.display_sk)
              : true,
            display_en: newsDetail
              ? getBooleanValue(newsDetail.display_en)
              : false,
            valid_from: newsDetail ? dayjs(newsDetail.valid_from) : dayjs(),
            created_at: newsDetail?.created_at ? dayjs(newsDetail.created_at) : undefined,
            is_top: newsDetail ? getBooleanValue(newsDetail.top_to) : false,
            top_to:
              newsDetail && newsDetail.top_to ? dayjs(newsDetail.top_to) : null,
            categories: (newsDetail?.categories || []).map(({ id }) => id),
          },
    [isEdit, newsDetail, newsCategories, currentUserPersonIdForForm],
  );

  const title = isCreate ? t('Pridanie aktuality') : t('Úprava aktuality');

  if ((isEdit && isLoading) || !newsCategories || isLoadingNewsCategories) {
    return <Spinner />;
  }

  return (
    <>
      <AppBarCMS
        title={title}
        backUrl={routes.cmsNews}
        backUrlTooltip={t('Aktuality')}
        permissionsBasic={'news'}
      />
      <NewsForm
        initials={initials}
        categories={newsCategories}
        displayTopOptions={isNewsAdmin}
        isEdit={isEdit}
        creator={
          (newsDetail && newsDetail.creatorName) || formattedFullName(user)
        }
        handleSubmit={(values) => {
          const formData = {
            ...values,
          };

          if (!navigateTo.current) {
            navigateTo.current = routes.cmsNews;
          }
          if (location.state?.fromDetail) {
            let url = values[`slug_${language}`];
            navigateTo.current = url
              ? navigateTo.current.substr(
                  0,
                  navigateTo.current.lastIndexOf('/'),
                ) +
                '/' +
                url
              : routes.cmsNews;
          }

          // in case is_top is not defined
          if (!has(formData, 'is_top')) {
            formData.is_top = getBooleanValue(newsDetail.is_top);
          }
          saveNews({ data: prepareOutputData(formData), id: id });
        }}
        visibleElement={visibleElement}
        setVisibleElement={setVisibleElement}
        setFormDisabledStates={setFormDisabledStates}
      />
    </>
  );
};

export default NewsEdit;
